import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, AlertTriangle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';

interface CancelGoalModalProps {
  isOpen: boolean;
  goalName: string;
  goalAmount: number;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

const CancelGoalModal: React.FC<CancelGoalModalProps> = ({
  isOpen,
  goalName,
  goalAmount,
  onConfirm,
  onCancel
}) => {
  const { theme } = useTheme();
  const [reason, setReason] = useState('');
  const [selectedPreset, setSelectedPreset] = useState('');

  const presetReasons = [
    'Emergência financeira',
    'Mudança de prioridades',
    'Meta atingida por outra forma',
    'Valor muito alto',
    'Prazo muito longo',
    'Outro motivo'
  ];

  const handlePresetSelect = (preset: string) => {
    setSelectedPreset(preset);
    if (preset !== 'Outro motivo') {
      setReason(preset);
    } else {
      setReason('');
    }
  };

  const handleConfirm = () => {
    if (!reason.trim()) {
      alert('Por favor, informe o motivo do cancelamento');
      return;
    }
    onConfirm(reason);
    setReason('');
    setSelectedPreset('');
  };

  const handleCancel = () => {
    setReason('');
    setSelectedPreset('');
    onCancel();
  };

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleCancel}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.2 }}
            className={`relative rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl ${
              theme === 'dark'
                ? 'bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700/50'
                : 'bg-white border border-gray-200'
            }`}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-500/20 rounded-full">
                  <AlertTriangle className="text-red-400" size={20} />
                </div>
                <h2 className={`text-xl font-semibold ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  Cancelar Meta
                </h2>
              </div>
              <button
                onClick={handleCancel}
                className={`p-2 rounded-lg transition-all duration-200 ${
                  theme === 'dark'
                    ? 'text-gray-400 hover:text-white hover:bg-slate-700/50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <X size={20} />
              </button>
            </div>

            {/* Informações da Meta */}
            <div className={`mb-6 p-4 rounded-lg ${
              theme === 'dark' ? 'bg-slate-700/50' : 'bg-gray-50'
            }`}>
              <p className={`text-sm font-medium mb-2 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Você está cancelando:
              </p>
              <p className={`text-lg font-bold mb-1 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                {goalName}
              </p>
              <p className={`text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Valor acumulado: <span className="font-semibold text-emerald-500">
                  R$ {goalAmount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              </p>
            </div>

            {/* Aviso */}
            <div className={`mb-4 p-4 rounded-lg border ${
              theme === 'dark'
                ? 'bg-yellow-900/20 border-yellow-500/30'
                : 'bg-yellow-50 border-yellow-200'
            }`}>
              <p className={`text-sm ${
                theme === 'dark' ? 'text-yellow-200' : 'text-yellow-800'
              }`}>
                ⚠️ As transações relacionadas ficarão visíveis com o status "CANCELADA" 
                e o valor será reembolsado.
              </p>
            </div>

            {/* Motivos pré-definidos */}
            <div className="mb-4">
              <label className={`block text-sm font-medium mb-3 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Motivo do cancelamento:
              </label>
              <div className="grid grid-cols-2 gap-2 mb-3">
                {presetReasons.map((preset) => (
                  <button
                    key={preset}
                    onClick={() => handlePresetSelect(preset)}
                    className={`px-3 py-2 text-sm rounded-lg transition-all duration-200 text-left ${
                      selectedPreset === preset
                        ? theme === 'dark'
                          ? 'bg-blue-600 text-white border-2 border-blue-400'
                          : 'bg-blue-100 text-blue-900 border-2 border-blue-400'
                        : theme === 'dark'
                          ? 'bg-slate-700/50 text-gray-300 border border-slate-600 hover:bg-slate-600/50'
                          : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                    }`}
                  >
                    {preset}
                  </button>
                ))}
              </div>
            </div>

            {/* Campo de texto customizado */}
            {(selectedPreset === 'Outro motivo' || selectedPreset === '') && (
              <div className="mb-6">
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Ou descreva o motivo:
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Ex: Precisei usar o dinheiro para uma emergência..."
                  rows={3}
                  className={`w-full px-4 py-3 rounded-lg transition-all duration-200 ${
                    theme === 'dark'
                      ? 'bg-slate-700/50 border border-slate-600/50 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20'
                      : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20'
                  }`}
                />
              </div>
            )}

            {/* Botões */}
            <div className="flex gap-3">
              <button
                onClick={handleCancel}
                className={`flex-1 px-4 py-3 font-medium rounded-lg transition-all duration-200 ${
                  theme === 'dark'
                    ? 'bg-slate-700/50 hover:bg-slate-600/50 text-gray-300'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                Voltar
              </button>
              <button
                onClick={handleConfirm}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600 text-white font-medium rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                Confirmar Cancelamento
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  if (typeof document !== 'undefined') {
    return createPortal(modalContent, document.body);
  }

  return modalContent;
};

export default CancelGoalModal;
